import { forwardRef, useImperativeHandle, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useWarehouseItems, useLowStockCount } from '@/hooks/warehouse';
import WarehouseTable from './WarehouseTable';
import WarehouseFilters from './WarehouseFilters';
import AddToWarehouseModal from './AddToWarehouseModal';

export interface WarehouseListViewRef {
  createNew: () => void;
}

const WarehouseListView = forwardRef<WarehouseListViewRef>((_, ref) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [category, setCategory] = useState('');
  
  const { data: items = [], isLoading } = useWarehouseItems();
  const lowStockCount = useLowStockCount();

  useImperativeHandle(ref, () => ({
    createNew: () => setIsModalOpen(true),
  }));

  const handleClearFilters = () => {
    setSearchTerm('');
    setOrganizationId('');
    setCategory('');
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <WarehouseFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        organizationId={organizationId}
        onOrganizationChange={setOrganizationId}
        category={category}
        onCategoryChange={setCategory}
        onClear={handleClearFilters}
      />

      {/* Low stock filter */}
      {lowStockCount > 0 && (
        <div className="flex items-center gap-2">
          <Button
            variant={showLowStockOnly ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowLowStockOnly(!showLowStockOnly)}
            className="flex items-center gap-2"
          >
            <AlertTriangle size={14} />
            Stock Bajo
            <Badge variant="secondary" className="ml-1">
              {lowStockCount}
            </Badge>
          </Button>
          {showLowStockOnly && (
            <span className="text-sm text-muted-foreground">
              Mostrando solo productos con stock bajo
            </span>
          )}
        </div>
      )}

      <WarehouseTable
        items={items}
        isLoading={isLoading}
        showOnlyLowStock={showLowStockOnly}
        searchTerm={searchTerm}
        organizationId={organizationId}
        category={category}
      />

      <AddToWarehouseModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
});

WarehouseListView.displayName = 'WarehouseListView';

export default WarehouseListView;
